import { useEffect } from 'react';

import { fontFor, googleFontsHref } from '@/lib/fonts';

/**
 * What the typeface actually looks like, before it is applied to the shop.
 *
 * ── WHY THIS IS WORTH A COMPONENT ───────────────────────────────────────────
 *
 * A dropdown of font names asks somebody to choose between nine things they
 * cannot see. "Manrope" and "Figtree" are not words that mean anything until
 * you are looking at them, so without this the only way to compare two of them
 * is to save one, go and look at the shop, come back and save the other — with
 * every buyer on the site seeing each guess along the way.
 *
 * So the chosen family is fetched and shown here, at the sizes it will actually
 * be used at, the moment it is picked and before anything is saved.
 *
 * ── AND WHY THE SAMPLE SAYS WHAT IT SAYS ────────────────────────────────────
 *
 * Not "The quick brown fox". A pangram tests a font's coverage; it does not
 * tell you whether THIS product will be readable. The sample is a product name,
 * a price and a warning line — the three things this typeface will spend its
 * life setting — so what is being compared is the actual job.
 */
export function FontPreview({ fontKey, role }: { fontKey: string; role: 'display' | 'body' }) {
  const choice = fontFor(fontKey, role === 'display' ? 'outfit' : 'plus-jakarta');

  useEffect(() => {
    const href = googleFontsHref([choice]);

    if (!href) return;

    // One link per family, reused across every preview on the screen. Both
    // fields are usually being compared at once, and two copies of the same
    // stylesheet is a second request for bytes already in flight.
    const id = `font-preview-${choice.family.replace(/ /g, '-')}`;

    if (document.getElementById(id)) return;

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.append(link);

    // No cleanup: a stylesheet already fetched costs nothing to leave, and
    // removing it would blank a preview the moment two fields share a family.
  }, [choice]);

  return (
    <div
      className="mt-2 rounded-card border border-line bg-paper px-3.5 py-3"
      style={{ fontFamily: choice.stack }}
    >
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        {choice.family || 'System font'}
      </p>

      {role === 'display' ? (
        <>
          <p className="m-0 mt-1.5 text-[19px] font-bold leading-tight tracking-[-0.02em] text-ink">
            Paracetamol 500mg
          </p>
          <p className="m-0 mt-0.5 text-[15px] font-bold text-ink">₦1,450 · pack of 21</p>
        </>
      ) : (
        <>
          <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-ink">
            Every pharmacy on ChiCare is PCN-licensed and checked by hand. Compare the batch
            number on this page against the pack before you pay the rider.
          </p>
          {/* At 11.5px, because that is where a typeface either holds up on a
              cheap screen or quietly stops being readable. */}
          <p className="m-0 mt-1.5 text-[11.5px] leading-snug text-muted">
            Prescription only — a pharmacist will check your prescription before dispensing.
          </p>
        </>
      )}
    </div>
  );
}
